// src/utils/Adjust.js

const EMPTY_ADJUSTMENT = {
  bet: "0",
  win: "0",
  tie: "0",
  cancle: "0",
  wl: "0",
  rtp: "0",
  overamount: "0",
  betCount: "0",
};

// totalLogs 배열 참조 기준으로 vendor 결과 캐시
const vendorAdjustmentCache = new WeakMap();
// totalLogs 배열 + vendors 배열 참조 기준으로 type 결과 캐시
const typeAdjustmentCache = new WeakMap();

/**
 * 전체 vendors 와 totalLogs, targetType 에 따라 집계값을 계산하는 훅
 * @param {Array} vendors
 * @param {Array} totalLogs
 * @param {string} targetType – "all"이거나 vendors[].v_type 값
 */
export function GetAdjustmentByType(vendors, totalLogs, targetType) {
  if (!totalLogs || !Array.isArray(vendors)) {
    return EMPTY_ADJUSTMENT;
  }

  let vendorsCache = typeAdjustmentCache.get(totalLogs);
  if (!vendorsCache) {
    vendorsCache = new WeakMap();
    typeAdjustmentCache.set(totalLogs, vendorsCache);
  }

  let targetTypeMap = vendorsCache.get(vendors);
  if (!targetTypeMap) {
    targetTypeMap = new Map();
    vendorsCache.set(vendors, targetTypeMap);
  }

  if (targetTypeMap.has(targetType)) {
    return targetTypeMap.get(targetType);
  }

  const matchedVendors =
    targetType === "all"
      ? vendors
      : vendors.filter((v) => v.v_type === targetType);
  const matchedCodes = matchedVendors.map((v) => v.v_code);

  const filtered = totalLogs.filter((l) => matchedCodes.includes(l.vendor));

  // roundId 별 그룹핑
  const roundGroups = {};
  filtered.forEach((l) => {
    if (!roundGroups[l.roundId]) roundGroups[l.roundId] = [];
    roundGroups[l.roundId].push(l);
  });

  // tie = 그룹 합계가 0 이고 cancle 타입 없는 라운드에서, positive amount 합계
  const tie = Object.values(roundGroups)
    .filter(
      (group) =>
        group.reduce((sum, item) => sum + Number(item.amount), 0) === 0 &&
        !group.some((item) => item.type === "cancle")
    )
    .flat()
    .filter((item) => Number(item.amount) > 0)
    .reduce((sum, item) => sum + Number(item.amount), 0);

  const cancle = filtered
    .filter((item) => item.type === "cancle")
    .reduce((sum, item) => sum + Math.abs(Number(item.amount)), 0);

  const bet =
    filtered
      .filter((item) => Number(item.amount) < 0)
      .reduce((sum, item) => sum + Math.abs(Number(item.amount)), 0) -
    tie -
    cancle;

  const win =
    filtered
      .filter((item) => Number(item.amount) > 0)
      .reduce((sum, item) => sum + Math.abs(Number(item.amount)), 0) -
    tie -
    cancle;

  const wl = bet - (win + tie + cancle);
  const rtp = bet > 0 ? ((win + tie + cancle) / bet) * 100 : 0;
  const betCount = filtered.filter((item) => item.type === "bet").length;

  const fmt = (num) => num.toLocaleString();
  const fmtRtp = Number.isFinite(rtp)
    ? Number.isInteger(rtp)
      ? fmt(rtp)
      : rtp.toFixed(2).toLocaleString()
    : "0";

  const result = {
    bet: fmt(bet),
    win: fmt(win),
    tie: fmt(tie),
    cancle: fmt(cancle),
    wl: fmt(wl),
    rtp: fmtRtp,
    overamount: fmt(0),
    betCount: fmt(betCount),
  };
  targetTypeMap.set(targetType, result);
  return result;
}

/**
 * 단일 vendor 객체와 totalLogs 에 따라 집계값을 계산하는 훅
 * @param {{ v_code: string }} vendor
 * @param {Array} totalLogs
 */
export function GetAdjustment(vendor, totalLogs) {
  if (!totalLogs) {
    return EMPTY_ADJUSTMENT;
  }

  let logsCache = vendorAdjustmentCache.get(totalLogs);
  if (!logsCache) {
    logsCache = new Map();
    vendorAdjustmentCache.set(totalLogs, logsCache);
  }

  if (logsCache.has(vendor.v_code)) {
    return logsCache.get(vendor.v_code);
  }

  const filtered = totalLogs.filter((l) => l.vendor === vendor.v_code);

  // roundId 별 그룹핑
  const roundGroups = {};
  filtered.forEach((l) => {
    if (!roundGroups[l.roundId]) roundGroups[l.roundId] = [];
    roundGroups[l.roundId].push(l);
  });

  const tie = Object.values(roundGroups)
    .filter(
      (group) =>
        group.reduce((sum, item) => sum + Number(item.amount), 0) === 0 &&
        !group.some((item) => item.type === "cancle")
    )
    .flat()
    .filter((item) => Number(item.amount) > 0)
    .reduce((sum, item) => sum + Number(item.amount), 0);

  const cancle = filtered
    .filter((item) => item.type === "cancle")
    .reduce((sum, item) => sum + Math.abs(Number(item.amount)), 0);

  const bet =
    filtered
      .filter((item) => Number(item.amount) < 0)
      .reduce((sum, item) => sum + Math.abs(Number(item.amount)), 0) -
    tie -
    cancle;

  const win =
    filtered
      .filter((item) => Number(item.amount) > 0)
      .reduce((sum, item) => sum + Math.abs(Number(item.amount)), 0) -
    tie -
    cancle;

  const wl = bet - (win + tie + cancle);
  const rtp = bet > 0 ? ((win + tie + cancle) / bet) * 100 : 0;
  const betCount = totalLogs.filter(
    (l) => l.vendor === vendor.v_code && l.type === "bet"
  ).length;

  const fmt = (num) => num.toLocaleString();
  const fmtRtp = Number.isFinite(rtp)
    ? Number.isInteger(rtp)
      ? fmt(rtp)
      : rtp.toFixed(2).toLocaleString()
    : "0";

  const result = {
    bet: fmt(bet),
    win: fmt(win),
    tie: fmt(tie),
    cancle: fmt(cancle),
    wl: fmt(wl),
    rtp: fmtRtp,
    overamount: fmt(0),
    betCount: fmt(betCount),
  };
  logsCache.set(vendor.v_code, result);
  return result;
}
